import { useState, useEffect, useCallback } from 'react';
import StepUpDialog from '../components/StepUpDialog';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import MobileNavbar from '../components/MobileNavbar';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Building2, Plus, Pencil, Trash2, Eye, EyeOff, LogIn, Download, Users, Landmark, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../services/api';
import { UploadInput } from '../components/UploadInput';
import { useIsUploading } from '../stores/uploadStatusStore';

const CompaniesPage = () => {
  const { user, login: authLogin } = useAuth();
  const navigate = useNavigate();
  const isUploading = useIsUploading();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    name: '', adminUsername: '', adminPassword: '', adminName: '', adminEmail: '',
    logo: '', address: '', planInfo: '', planStatus: 'Active', planStartDate: '', planEndDate: '', maxVendors: 0, maxBanks: 0
  });
  const [editForm, setEditForm] = useState({});

  const fetchCompanies = useCallback(async () => {
    try { const res = await api.getCompanies(); setCompanies(res.data); }
    catch { toast.error('Failed to load companies'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const handleCreate = async () => {
    if (!form.name?.trim() || !form.adminUsername?.trim() || !form.adminPassword?.trim()) {
      toast.error('Company name, admin username and password are required'); return;
    }
    try {
      await api.createCompany(form);
      toast.success('Company created successfully');
      setShowCreateDialog(false);
      setForm({ name: '', adminUsername: '', adminPassword: '', adminName: '', adminEmail: '', logo: '', address: '', planInfo: '', planStatus: 'Active', planStartDate: '', planEndDate: '', maxVendors: 0, maxBanks: 0 });
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create company'); }
  };

  const handleEdit = async () => {
    try {
      await api.updateCompany(selectedCompany.id, editForm);
      toast.success('Company updated');
      setShowEditDialog(false);
      fetchCompanies();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (requestId, code) => {
    await api.deleteCompany(selectedCompany.id, requestId, code);
    toast.success('Company deleted. A backup was emailed first.');
    fetchCompanies();
  };

  const handleImpersonate = async (company) => {
    try {
      const res = await api.impersonateAdmin(company.id);
      sessionStorage.setItem('superAdminToken', sessionStorage.getItem('token'));
      sessionStorage.setItem('superAdminUser', JSON.stringify(user));
      sessionStorage.setItem('token', res.data.token);
      sessionStorage.setItem('impersonating', JSON.stringify({ companyName: company.name, adminName: res.data.user?.username }));
      window.location.href = '/dashboard';
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to impersonate'); }
  };

  const openEdit = (company) => {
    setSelectedCompany(company);
    setEditForm({
      name: company.name, logo: company.logo || '', address: company.address || '',
      planInfo: company.planInfo || '', planStatus: company.planStatus || 'Active',
      planStartDate: company.planStartDate || '', planEndDate: company.planEndDate || '',
      maxVendors: company.maxVendors || 0, maxBanks: company.maxBanks || 0,
      adminEmail: company.adminEmail || ''
    });
    setShowEditDialog(true);
  };

  const handleExportAll = async (format) => {
    try {
      const res = await api.exportAllBanksFile(format);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `All_Companies_Banks.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} exported`);
    } catch { toast.error('Export failed'); }
  };

  const totals = companies.reduce((acc, c) => ({
    vendors: acc.vendors + (c.vendorCount || 0),
    users: acc.users + (c.userCount || 0),
    banks: acc.banks + (c.bankCount || 0),
    active: acc.active + (c.activeBanks || 0),
    freezed: acc.freezed + (c.freezedBanks || 0),
    closed: acc.closed + (c.closedBanks || 0),
  }), { vendors: 0, users: 0, banks: 0, active: 0, freezed: 0, closed: 0 });

  const getPlanBadge = (status) => {
    const styles = { Active: 'bg-green-100 text-green-800', Expired: 'bg-red-100 text-red-800', Trial: 'bg-amber-100 text-amber-800' };
    return <Badge className={styles[status] || 'bg-slate-100 text-slate-800'}>{status || 'Active'}</Badge>;
  };

  if (user?.role !== 'Super Admin') return <div className="p-8 text-center text-slate-600">Access denied.</div>;

  return (
    <>
      <Navbar />
      <MobileNavbar />
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6 mobile-content-wrapper">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Companies</h1>
            <p className="text-slate-600 mt-1 text-sm">Manage companies and their admin accounts</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => handleExportAll('excel')} className="gap-1"><Download className="w-4 h-4" />Excel</Button>
            <Button variant="outline" onClick={() => handleExportAll('pdf')} className="gap-1"><Download className="w-4 h-4" />PDF</Button>
            <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-company-btn"><Plus className="w-4 h-4" /> New Company</Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="border-l-4 border-l-blue-500"><CardContent className="pt-4"><div className="text-xs text-slate-600">Companies</div><div className="text-2xl font-bold">{companies.length}</div></CardContent></Card>
          <Card className="border-l-4 border-l-indigo-500"><CardContent className="pt-4"><div className="text-xs text-slate-600">Total Banks</div><div className="text-2xl font-bold text-indigo-600">{totals.banks}</div></CardContent></Card>
          <Card className="border-l-4 border-l-green-500"><CardContent className="pt-4"><div className="text-xs text-slate-600">Active</div><div className="text-2xl font-bold text-green-600">{totals.active}</div></CardContent></Card>
          <Card className="border-l-4 border-l-red-500"><CardContent className="pt-4"><div className="text-xs text-slate-600">Freezed</div><div className="text-2xl font-bold text-red-600">{totals.freezed}</div></CardContent></Card>
          <Card className="border-l-4 border-l-slate-400"><CardContent className="pt-4"><div className="text-xs text-slate-600">Closed</div><div className="text-2xl font-bold text-slate-500">{totals.closed}</div></CardContent></Card>
          <Card className="border-l-4 border-l-purple-500"><CardContent className="pt-4"><div className="text-xs text-slate-600">Total Users</div><div className="text-2xl font-bold text-purple-600">{totals.users}</div></CardContent></Card>
        </div>

        {/* Company-wise Bifurcation Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-semibold">Company</th>
                    <th className="text-left p-3 font-semibold">Admin</th>
                    <th className="text-left p-3 font-semibold">Plan</th>
                    <th className="text-center p-3 font-semibold">Vendors</th>
                    <th className="text-center p-3 font-semibold">Banks</th>
                    <th className="text-center p-3 font-semibold text-green-700">Active</th>
                    <th className="text-center p-3 font-semibold text-red-700">Freezed</th>
                    <th className="text-center p-3 font-semibold text-slate-500">Closed</th>
                    <th className="text-center p-3 font-semibold text-indigo-700">Assigned</th>
                    <th className="text-center p-3 font-semibold">Users</th>
                    <th className="text-right p-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map(company => (
                    <tr key={company.id} className="border-b hover:bg-slate-50" data-testid={`company-row-${company.id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {company.logo ? <img src={company.logo} alt="" className="w-8 h-8 rounded object-cover" /> : <Building2 className="w-5 h-5 text-blue-500" />}
                          <div>
                            <div className="font-medium">{company.name}</div>
                            {company.address && <div className="text-xs text-slate-400">{company.address}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3"><span className="font-medium">{company.admin?.username || '-'}</span></td>
                      <td className="p-3">{getPlanBadge(company.planStatus)}{company.planInfo && <span className="text-xs text-slate-500 ml-1">{company.planInfo}</span>}</td>
                      <td className="p-3 text-center font-semibold">{company.vendorCount || 0}</td>
                      <td className="p-3 text-center font-semibold">{company.bankCount || 0}</td>
                      <td className="p-3 text-center font-semibold text-green-600">{company.activeBanks || 0}</td>
                      <td className="p-3 text-center font-semibold text-red-600">{company.freezedBanks || 0}</td>
                      <td className="p-3 text-center text-slate-500">{company.closedBanks || 0}</td>
                      <td className="p-3 text-center font-semibold text-indigo-600">{company.assignedBanks || 0}</td>
                      <td className="p-3 text-center">{company.userCount || 0}</td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Login as Admin" onClick={() => handleImpersonate(company)} data-testid={`impersonate-${company.id}`}><LogIn className="w-4 h-4 text-emerald-600" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(company)} data-testid={`edit-company-${company.id}`}><Pencil className="w-4 h-4 text-blue-500" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setSelectedCompany(company); setShowDeleteDialog(true); }} data-testid={`delete-company-${company.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {companies.length === 0 && (
                    <tr><td colSpan="11" className="p-12 text-center text-slate-500"><Building2 className="w-10 h-10 mx-auto text-slate-300 mb-2" />No companies yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Create New Company</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Company Name *</Label><Input data-testid="company-name-input" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Enter company name" /></div>
            <div>
              <Label>Company Logo</Label>
              <UploadInput onUpload={(b) => setForm(p => ({ ...p, logo: b }))} />
              {form.logo && <img src={form.logo} alt="Preview" className="w-16 h-16 mt-2 rounded object-cover border" />}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Admin Username *</Label><Input data-testid="admin-username-input" value={form.adminUsername} onChange={(e) => setForm(p => ({ ...p, adminUsername: e.target.value }))} /></div>
              <div>
                <Label>Admin Password *</Label>
                <div className="relative">
                  <Input data-testid="admin-password-input" type={showPassword ? 'text' : 'password'} value={form.adminPassword} onChange={(e) => setForm(p => ({ ...p, adminPassword: e.target.value }))} />
                  <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1 h-7 w-7 p-0" onClick={() => setShowPassword(!showPassword)}>{showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}</Button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Admin Name</Label><Input value={form.adminName} onChange={(e) => setForm(p => ({ ...p, adminName: e.target.value }))} /></div>
              <div><Label>Admin Email</Label><Input value={form.adminEmail} onChange={(e) => setForm(p => ({ ...p, adminEmail: e.target.value }))} /></div>
            </div>
            <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))} /></div>
            <div className="border-t pt-4"><h4 className="text-sm font-semibold text-slate-700 mb-3">Subscription</h4></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Plan Name</Label><Input value={form.planInfo} onChange={(e) => setForm(p => ({ ...p, planInfo: e.target.value }))} placeholder="e.g. Pro" /></div>
              <div><Label>Plan Status</Label>
                <Select value={form.planStatus} onValueChange={(v) => setForm(p => ({ ...p, planStatus: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Trial">Trial</SelectItem><SelectItem value="Expired">Expired</SelectItem></SelectContent>
                </Select>
              </div>
              <div></div>
              <div><Label>Start Date</Label><Input type="date" value={form.planStartDate} onChange={(e) => setForm(p => ({ ...p, planStartDate: e.target.value }))} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.planEndDate} onChange={(e) => setForm(p => ({ ...p, planEndDate: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={isUploading} title={isUploading ? 'Wait for uploads to finish' : ''} data-testid="submit-create-company">{isUploading ? 'Uploading…' : 'Create Company'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit: {selectedCompany?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Company Name</Label><Input value={editForm.name || ''} onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))} /></div>
            <div>
              <Label>Company Logo</Label>
              <UploadInput onUpload={(b) => setEditForm(p => ({ ...p, logo: b }))} />
              {(editForm.logo || selectedCompany?.logo) && <img src={editForm.logo || selectedCompany?.logo} alt="Logo" className="w-16 h-16 mt-2 rounded object-cover border" />}
            </div>
            <div><Label>Admin Email</Label><Input value={editForm.adminEmail || ''} onChange={(e) => setEditForm(p => ({ ...p, adminEmail: e.target.value }))} /></div>
            <div><Label>Address</Label><Input value={editForm.address || ''} onChange={(e) => setEditForm(p => ({ ...p, address: e.target.value }))} /></div>
            <div className="border-t pt-4"><h4 className="text-sm font-semibold text-slate-700 mb-3">Subscription</h4></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Plan Name</Label><Input value={editForm.planInfo || ''} onChange={(e) => setEditForm(p => ({ ...p, planInfo: e.target.value }))} /></div>
              <div><Label>Plan Status</Label>
                <Select value={editForm.planStatus || 'Active'} onValueChange={(v) => setEditForm(p => ({ ...p, planStatus: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Trial">Trial</SelectItem><SelectItem value="Expired">Expired</SelectItem></SelectContent>
                </Select>
              </div>
              <div></div>
              <div><Label>Start Date</Label><Input type="date" value={editForm.planStartDate || ''} onChange={(e) => setEditForm(p => ({ ...p, planStartDate: e.target.value }))} /></div>
              <div><Label>End Date</Label><Input type="date" value={editForm.planEndDate || ''} onChange={(e) => setEditForm(p => ({ ...p, planEndDate: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={isUploading} title={isUploading ? 'Wait for uploads to finish' : ''}>{isUploading ? 'Uploading…' : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <StepUpDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        action="delete_company"
        targetId={selectedCompany?.id || ''}
        targetLabel={selectedCompany?.name || ''}
        title="Delete Company"
        description={<>Permanently delete <strong>{selectedCompany?.name}</strong> and all its data. A backup of this company is emailed first. This cannot be undone.</>}
        confirmLabel="Delete Company"
        onConfirm={handleDelete}
        testIdPrefix="delete-company"
      />
    </>
  );
};

export default CompaniesPage;
